import type * as T from "three";
import { studioEnvironment } from "./studio";
import type { SceneSetup, Three } from "./useThreeCanvas";

/*
 * Silver IQ coins running along an arc, each one tumbling on its own axis (the prototype's `?cta=arco`).
 * The prototype drew the arc on a canvas covering the whole 1920×1021 section; here the canvas is only the
 * coins box, so the camera renders that band of the same virtual frame (setViewOffset).
 * Arc spread/height and phase are fitted to the Figma render (node 9390:716), ~10px RMS on the coin centres.
 */

const FRAME = { w: 1920, h: 1021, boxX: -18.718, boxY: 498, boxW: 1959.016 };
const FOV = 24;
const CAM_Z = 12;

const COINS = 9;
const ARC_SPREAD = 1.01;
const ARC_BASE = -0.4;
const COIN_SCALE = 0.6 * 0.97;
const SCROLL_SHIFT = 0.14; // how far scrolling into the section pushes the queue along the arc
// At rest (scrolled in, t = 0) coin i sits at Figma slot i + 1 and the last coin wraps into slot 0
const PHASE = 0.03 + 1 / COINS - SCROLL_SHIFT;

type Draw = (g: CanvasRenderingContext2D, R: (r: number) => number) => void;
interface Face {
  label: string;
  draw?: Draw;
  src?: string;
}

// The IQ mark from the logo (the I bar and the Q), 23×20
const IQ_PATHS = [
  "M0 17.8065V0.394287H4.25559V17.8065H0Z",
  "M18.7723 19.2508L17.451 17.6326C17.0938 17.8117 16.6415 17.949 16.1058 18.0505C15.5702 18.1461 14.975 18.1998 14.3203 18.1998C12.5942 18.1998 11.0824 17.8177 9.79684 17.0593C8.51124 16.301 7.51728 15.2321 6.81496 13.8587C6.11263 12.4853 5.76147 10.9089 5.76147 9.1235C5.76147 7.33809 6.08883 5.8393 6.73758 4.46591C7.38634 3.09252 8.35649 2.00575 9.63614 1.2056C10.9158 0.405446 12.4752 0.00537109 14.3203 0.00537109C16.1653 0.00537109 17.7188 0.405446 18.9865 1.2056C20.2602 2.00575 21.2185 3.09252 21.8732 4.46591C22.522 5.8393 22.8493 7.39183 22.8493 9.1235C22.8493 10.7417 22.5815 12.145 22.0399 13.3273C21.5042 14.5156 20.7423 15.5128 19.7662 16.3308L22.1113 19.2508H18.7723ZM14.3262 14.7126C14.5048 14.7126 14.6714 14.7007 14.8143 14.6768C14.9631 14.6529 15.1 14.6171 15.2309 14.5633L12.8621 11.6673H16.213L17.2903 13.0884C17.6652 12.6824 17.9569 12.1629 18.1711 11.5299C18.3854 10.9029 18.4866 10.0968 18.4866 9.11155C18.4866 8.1263 18.3437 7.18881 18.058 6.34089C17.7724 5.49297 17.326 4.8003 16.7129 4.26886C16.0999 3.73741 15.3083 3.47468 14.3322 3.47468C13.3561 3.47468 12.5347 3.74338 11.9217 4.26886C11.3086 4.8003 10.8622 5.49297 10.5765 6.34089C10.2909 7.18881 10.148 8.11435 10.148 9.11155C10.148 10.1088 10.2968 11.0104 10.5884 11.8583C10.8801 12.7063 11.3324 13.393 11.9455 13.9006C12.5585 14.4081 13.3561 14.7126 14.3262 14.7126Z",
];

// Emblems drawn as white strokes/fills (white = raised relief)
const drawIQ: Draw = (g, R) => {
  const k = R(0.5) / 20;
  g.save();
  g.translate(-11.4 * k, -9.8 * k);
  g.scale(k, k);
  IQ_PATHS.forEach((d) => g.fill(new Path2D(d)));
  g.restore();
};
// No crypto coins: the IQ mark and the trading platforms only. Coins take the faces in turn
// (coin i -> FACES[i % length]), so neighbours never repeat.
const FACES: Face[] = [
  { label: "IQ ELITE", draw: drawIQ },
  { label: "TRADOVATE", src: "/elite/coin-tradovate.png" },
  { label: "ATAS", src: "/elite/coin-atas.png" },
  { label: "METATRADER 5", src: "/elite/coin-metatrader5.png" },
  { label: "VOLUMETRICA", src: "/elite/coin-volumetrica.png" },
];

const loadImage = (src: string) =>
  new Promise<HTMLImageElement | null>((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
const nextFrame = () => new Promise((resolve) => requestAnimationFrame(resolve));
const canvas2d = (size: number): [HTMLCanvasElement, CanvasRenderingContext2D] => {
  const c = document.createElement("canvas");
  c.width = c.height = size;
  return [c, c.getContext("2d")!];
};

/** Coin body: lathed profile with a reeded edge, flat-shaded so every edge stays crisp. */
function coinGeometry(THREE: Three) {
  const profile = [
    [0.0, 0.05], [0.8, 0.05], [0.822, 0.084], [0.935, 0.088], [0.982, 0.072], [1.0, 0.052],
    [1.0, -0.052], [0.982, -0.072], [0.935, -0.088], [0.822, -0.084], [0.8, -0.05], [0.0, -0.05],
  ].map(([x, y]) => new THREE.Vector2(x, y));
  const GROOVES = 180;
  const lathe = new THREE.LatheGeometry(profile, GROOVES * 4);
  const pos = lathe.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const z = pos.getZ(i);
    if (Math.hypot(x, z) > 0.995 && Math.abs(pos.getY(i)) < 0.0525) {
      const c = Math.cos(Math.atan2(z, x) * GROOVES);
      const k = 1 + 0.005 * Math.sign(c) * Math.pow(Math.abs(c), 0.5);
      pos.setX(i, x * k);
      pos.setZ(i, z * k);
    }
  }
  lathe.dispose();
  const geo = lathe.toNonIndexed();
  geo.computeVertexNormals();
  geo.rotateX(Math.PI / 2); // standing, facing the camera
  // Planar UV; the back (z > 0) mirrors u so the engraving reads right on both sides
  const p = geo.attributes.position;
  const uv = new Float32Array(p.count * 2);
  for (let i = 0; i < p.count; i++) {
    const side = p.getZ(i) > 0 ? -1 : 1;
    uv[2 * i] = side * p.getX(i) * 0.5 + 0.5;
    uv[2 * i + 1] = p.getY(i) * 0.5 + 0.5;
  }
  geo.setAttribute("uv", new THREE.BufferAttribute(uv, 2));
  return geo;
}

/**
 * Proof-coin face: a frosted emblem in relief over a mirrored field, a legend around the rim, a thin bead
 * and some wear. Three maps from the same drawing: relief (bump), colour and roughness.
 */
function faceTextures(THREE: Three, face: Face, emblem: HTMLImageElement | null, font: string) {
  const N = 1536;
  const m = N / 2;
  const R = (r: number) => r * m;
  let seed = 7;
  const rnd = () => (seed = (seed * 16807) % 2147483647) / 2147483647;
  const scratches = Array.from({ length: 70 }, () => {
    const a = rnd() * 7;
    const r = R(0.1 + rnd() * 0.7);
    const L = R(0.01 + rnd() ** 2 * 0.12);
    const t = rnd() * 7;
    return [m + Math.cos(a) * r, m + Math.sin(a) * r, Math.cos(t) * L, Math.sin(t) * L];
  });

  // The emblem as a white mask (grey icons: grey becomes alpha)
  const M = 1024;
  const [mask, mg] = canvas2d(M);
  mg.translate(M / 2, M / 2);
  mg.fillStyle = mg.strokeStyle = "#fff";
  if (face.draw) face.draw(mg, (r) => ((r * M) / 2) * 1.3);
  else if (emblem) {
    const s = M * 0.54;
    mg.drawImage(emblem, -s / 2, -s / 2, s, s);
    const id = mg.getImageData(0, 0, M, M);
    const d = id.data;
    for (let i = 0; i < d.length; i += 4) {
      d[i + 3] = d[i];
      d[i] = d[i + 1] = d[i + 2] = 255;
    }
    mg.putImageData(id, 0, 0);
  }

  const legend = (g: CanvasRenderingContext2D, color: string) => {
    const text = `${face.label}  ·  IQ CAPITAL  ·  `;
    g.font = `600 ${Math.round(R(0.06))}px ${font}`;
    g.fillStyle = color;
    g.textAlign = "center";
    g.textBaseline = "middle";
    const widths = [...text].map((ch) => g.measureText(ch).width + R(0.016));
    const total = widths.reduce((a, b) => a + b, 0);
    const rr = R(0.72);
    let ang = -Math.PI / 2 - total / rr / 2;
    [...text].forEach((ch, i) => {
      const a = ang + widths[i] / 2 / rr;
      g.save();
      g.translate(m + Math.cos(a) * rr, m + Math.sin(a) * rr);
      g.rotate(a + Math.PI / 2);
      g.fillText(ch, 0, 0);
      g.restore();
      ang += widths[i] / rr;
    });
  };

  const draw = (kind: "rel" | "cor" | "rug") => {
    const L = kind === "rel" ? 1536 : 768;
    const k = L / N;
    const [c, g] = canvas2d(L);
    g.setTransform(k, 0, 0, k, 0, 0);
    const [field, art, detail] = { rel: [96, 0, 0], cor: [120, 210, 196], rug: [26, 235, 225] }[kind];
    const grey = (v: number) => `rgb(${v},${v},${v})`;
    g.fillStyle = grey(field);
    g.fillRect(0, 0, N, N);
    g.save();
    g.setTransform(-k, 0, 0, -k, L, L); // the face UV comes rotated 180°
    const s = R(1.62);
    if (kind === "rel") {
      // rounded relief: stacked mask layers with decreasing blur
      for (const [blur, a] of [[R(0.012), 0.4], [R(0.005), 0.35], [0, 0.25]]) {
        g.filter = blur ? `blur(${blur * k}px)` : "none";
        g.globalAlpha = a;
        g.drawImage(mask, m - s / 2, m - s / 2, s, s);
      }
      g.filter = "none";
      g.globalAlpha = 1;
    } else {
      const [tinted, tg] = canvas2d(M);
      tg.drawImage(mask, 0, 0);
      tg.globalCompositeOperation = "source-in";
      tg.fillStyle = grey(art);
      tg.fillRect(0, 0, M, M);
      g.drawImage(tinted, m - s / 2, m - s / 2, s, s);
    }
    legend(g, kind === "rel" ? "rgb(190,190,190)" : grey(detail));
    g.restore();
    // raised bead next to the rim
    g.strokeStyle = kind === "rel" ? "rgb(170,170,170)" : grey(detail);
    g.lineWidth = R(0.008);
    g.beginPath();
    g.arc(m, m, R(0.815), 0, 7);
    g.stroke();
    // wear: rare fine scratches on the mirror and a light haze
    if (kind !== "rel") {
      g.lineCap = "round";
      g.lineWidth = 1;
      for (const [x, y, dx, dy] of scratches) {
        g.strokeStyle = kind === "rug" ? "rgba(160,160,160,.35)" : "rgba(255,255,255,.12)";
        g.beginPath();
        g.moveTo(x, y);
        g.lineTo(x + dx, y + dy);
        g.stroke();
      }
      const haze = g.createRadialGradient(m, m, R(0.5), m, m, R(0.95));
      haze.addColorStop(0, "rgba(0,0,0,0)");
      haze.addColorStop(1, kind === "rug" ? "rgba(120,120,120,.35)" : "rgba(0,0,0,.15)");
      g.fillStyle = haze;
      g.fillRect(0, 0, N, N);
    }
    const tex = new THREE.CanvasTexture(c);
    tex.anisotropy = 8;
    tex.flipY = false;
    tex.colorSpace = kind === "cor" ? THREE.SRGBColorSpace : THREE.NoColorSpace;
    return tex;
  };
  return { rel: draw("rel"), cor: draw("cor"), rug: draw("rug") };
}

export const setupCoinArc: SceneSetup = async ({ THREE, renderer, canvas, container, reducedMotion, isCancelled }) => {
  renderer.shadowMap.enabled = true; // the relief casts a tiny shadow on the field
  renderer.shadowMap.type = THREE.PCFShadowMap;

  const scene = new THREE.Scene();
  scene.environment = studioEnvironment(THREE, renderer, {
    background: [1.4, 1.43, 1.4],
    topLeft: 40,
    right: 14,
    backLeft: 10,
    frontTop: 5,
    frontBottom: 0.18,
  });
  const key = new THREE.DirectionalLight(0xffffff, 1.5);
  key.position.set(-4, 5, 6);
  key.castShadow = true;
  key.shadow.mapSize.set(1024, 1024);
  key.shadow.bias = -0.0004;
  key.shadow.normalBias = 0.01;
  key.shadow.radius = 2;
  const rim = new THREE.DirectionalLight(0xfff1d6, 2.2);
  rim.position.set(3, 1.5, -2.5);
  const fill = new THREE.DirectionalLight(0xffffff, 1.2);
  fill.position.set(-3, -2, 4);
  scene.add(key, rim, fill);

  const cam = new THREE.PerspectiveCamera(FOV, FRAME.w / FRAME.h, 0.1, 80);
  cam.position.set(0, 0, CAM_Z);
  const visH = 2 * CAM_Z * Math.tan(THREE.MathUtils.degToRad(FOV / 2));
  const visW = visH * (FRAME.w / FRAME.h);
  const sc = key.shadow.camera;
  Object.assign(sc, { left: -visW / 2 - 1, right: visW / 2 + 1, top: visH / 2 + 1, bottom: -visH / 2 - 1, near: 0.5, far: 30 });
  sc.updateProjectionMatrix();

  const root = new THREE.Group();
  scene.add(root);

  // Faces: one texture set per distinct face, built one per frame so the page never stalls
  const font = getComputedStyle(document.body).fontFamily || "Arial, sans-serif";
  await document.fonts?.ready;
  const images = await Promise.all(FACES.map((f) => (f.src ? loadImage(f.src) : Promise.resolve(null))));
  const geo = coinGeometry(THREE);
  const cache = new Map<string, T.MeshPhysicalMaterial>();
  const coins: T.Mesh[] = [];
  for (let i = 0; i < COINS; i++) {
    const face = FACES[i % FACES.length];
    let mat = cache.get(face.label);
    if (!mat) {
      const tex = faceTextures(THREE, face, images[i % FACES.length], font);
      // satin silver: graphite field, light emblem and legend, relief from the bump map
      mat = new THREE.MeshPhysicalMaterial({
        color: 0xe4e6e8, metalness: 1, roughness: 0.9, clearcoat: 0.3, clearcoatRoughness: 0.12, envMapIntensity: 1,
        map: tex.cor, roughnessMap: tex.rug, bumpMap: tex.rel, bumpScale: 5, side: THREE.DoubleSide,
      });
      cache.set(face.label, mat);
      await nextFrame();
    }
    const coin = new THREE.Mesh(geo, mat);
    coin.castShadow = coin.receiveShadow = true;
    root.add(coin);
    coins.push(coin);
    if (isCancelled()) break;
  }
  if (isCancelled()) return { scene, camera: cam, resize: () => {} };

  // Drag a coin to spin it on its own axis, with inertia
  const state = coins.map(() => ({ spin: 0, vel: 0, sx: 0, sy: 0 }));
  let frameH = 1;
  let drag: { i: number; x: number } | null = null;
  const pick = (e: PointerEvent) => {
    const r = canvas.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    let best = -1;
    let dist = frameH * 0.14;
    state.forEach((s, i) => {
      const d = Math.hypot(s.sx - x, s.sy - y);
      if (d < dist) {
        dist = d;
        best = i;
      }
    });
    return best;
  };
  const onDown = (e: PointerEvent) => {
    const i = pick(e);
    if (i < 0) return;
    drag = { i, x: e.clientX };
    state[i].vel = 0;
    canvas.setPointerCapture(e.pointerId);
    canvas.style.cursor = "grabbing";
  };
  const onMove = (e: PointerEvent) => {
    if (drag) {
      const dx = e.clientX - drag.x;
      drag.x = e.clientX;
      state[drag.i].spin += dx * 0.014;
      state[drag.i].vel = dx * 0.014;
      return;
    }
    canvas.style.cursor = pick(e) >= 0 ? "grab" : "";
  };
  const onUp = () => {
    drag = null;
    canvas.style.cursor = "";
  };
  // Subtle parallax with the pointer anywhere on the page
  const mouse = { x: 0, y: 0, ax: 0, ay: 0 };
  const onWindowMove = (e: PointerEvent) => {
    mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
    mouse.y = (e.clientY / window.innerHeight) * 2 - 1;
  };
  if (!reducedMotion) {
    canvas.style.touchAction = "pan-y";
    canvas.addEventListener("pointerdown", onDown);
    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerup", onUp);
    canvas.addEventListener("pointercancel", onUp);
    window.addEventListener("pointermove", onWindowMove);
  }

  // Scrolling the section in (top from viewport bottom to 4%) pushes the queue along the arc
  const section = container.parentElement ?? container;
  const scrollTarget = () => {
    if (reducedMotion) return 1;
    const top = section.getBoundingClientRect().top;
    const vh = window.innerHeight;
    return Math.min(1, Math.max(0, (vh - top) / (vh * 0.96)));
  };
  let scroll = scrollTarget();

  const ndc = new THREE.Vector3();
  const smooth = (x: number) => x * x * (3 - 2 * x);
  const update = (dt: number, t: number) => {
    scroll += (scrollTarget() - scroll) * Math.min(1, dt * 3);
    mouse.ax += (mouse.x - mouse.ax) * 0.05;
    mouse.ay += (mouse.y - mouse.ay) * 0.05;
    root.rotation.set(mouse.ay * 0.05, mouse.ax * 0.08, 0);
    root.updateMatrixWorld();
    coins.forEach((coin, i) => {
      const s = ((((i / COINS + PHASE + t * 0.016 + scroll * SCROLL_SHIFT) % 1) + 1) % 1);
      const b = 1 - (2 * s - 1) ** 2; // 0 at the ends, 1 at the apex
      const u = state[i];
      if (!drag || drag.i !== i) {
        u.spin += u.vel;
        u.vel *= 0.955;
      }
      coin.position.set((-0.6 + 1.2 * s) * ARC_SPREAD * visW, (ARC_BASE + 0.26 * b) * visH, -1.6 + 2 * b);
      // shrink away at the wrap point so a coin never pops at the box edge
      const edge = smooth(Math.min(1, s / 0.02)) * smooth(Math.min(1, (1 - s) / 0.02));
      coin.scale.setScalar(COIN_SCALE * Math.max(edge, 0.001));
      coin.rotation.set(0.6 + 0.2 * Math.sin(i * 1.7), t * 0.5 + i * 0.9 + u.spin, 0.35 * Math.cos(i * 2.3) - 0.6 * (s - 0.5));
      ndc.copy(coin.position).applyMatrix4(root.matrixWorld).project(cam);
      u.sx = (ndc.x * 0.5 + 0.5) * canvas.clientWidth;
      u.sy = (0.5 - ndc.y * 0.5) * canvas.clientHeight;
    });
  };

  const resize = (w: number, h: number) => {
    const k = w / FRAME.boxW; // CSS px per design px
    frameH = FRAME.h * k;
    cam.setViewOffset(FRAME.w * k, frameH, FRAME.boxX * k, FRAME.boxY * k, w, h);
    cam.updateProjectionMatrix();
  };

  const dispose = () => {
    canvas.removeEventListener("pointerdown", onDown);
    canvas.removeEventListener("pointermove", onMove);
    canvas.removeEventListener("pointerup", onUp);
    canvas.removeEventListener("pointercancel", onUp);
    window.removeEventListener("pointermove", onWindowMove);
  };

  return { scene, camera: cam, update, resize, dispose };
};
